import React, { useState, useRef, useEffect, forwardRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  List,
  ListOrdered,
  Quote,
  Code2,
  Undo2,
  Redo2,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link2,
  Unlink,
  ChevronDown,
  Type,
  Image as ImageIcon,
  Upload,
  X,
} from "lucide-react";

import "./SimpleTiptapEditor.scss";
import { uploadMedia } from "../../../utils/MediaUploader";

const SimpleTiptapEditor = ({ 
  setContent, 
  initialContent = "",
  navbarHeight = 60,
  debounceDelay = 500,
}) => {
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentUpload, setCurrentUpload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  const headingMenuRef = useRef(null);
  const listMenuRef = useRef(null);
  const alignMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadAbortController = useRef(null);
  const debounceTimerRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5],
        },
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: "Start writing something amazing...",
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
    ],
    content: initialContent || "<p>Start writing...</p>",
    editorProps: {
      attributes: {
        class: "simple-editor",
        "aria-label": "Rich text editor",
        role: "textbox",
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          event.preventDefault();
          handleFiles(Array.from(event.dataTransfer.files));
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        if (event.clipboardData?.files?.length) {
          event.preventDefault();
          handleFiles(Array.from(event.clipboardData.files));
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      if (!setContent) return;
      
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      debounceTimerRef.current = setTimeout(() => {
        const html = editor.getHTML();
        setContent(html);
      }, debounceDelay);
    },
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (uploadAbortController.current) {
        uploadAbortController.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--navbar-height', `${navbarHeight}px`);
  }, [navbarHeight]);

  useEffect(() => {
    if (uploadQueue.length > 0 && !currentUpload) {
      processNextUpload();
    }
  }, [uploadQueue, currentUpload]);

  const processNextUpload = async () => {
    if (uploadQueue.length === 0) return;
    
    const nextFile = uploadQueue[0];
    setCurrentUpload(nextFile);
    await uploadImage(nextFile);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headingMenuRef.current && !headingMenuRef.current.contains(event.target)) {
        setShowHeadingMenu(false);
      }
      if (listMenuRef.current && !listMenuRef.current.contains(event.target)) {
        setShowListMenu(false);
      }
      if (alignMenuRef.current && !alignMenuRef.current.contains(event.target)) {
        setShowAlignMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!editor) {
    return (
      <div className="editor-error">
        <p>Failed to initialize editor. Please refresh the page.</p>
      </div>
    );
  }

  const validateImage = (file) => {
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/jpg'];
    
    if (!validImageTypes.includes(file.type)) {
      throw new Error(`Unsupported image type: ${file.type}. Please upload JPEG, PNG, GIF, WEBP, or SVG images.`);
    }
    
    const maxSize = 5 * 1024 * 1024; // 5MB for images
    
    if (file.size > maxSize) {
      throw new Error(`Image too large! Maximum size: 5MB`);
    }
    
    return true;
  };

  const uploadImage = async (file) => {
    setError(null);
    setUploadProgress(0);
    
    try {
      validateImage(file);
      
      uploadAbortController.current = new AbortController();
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);
      
      let imageUrl;
      let imageName = file.name;
      
      // Show local preview immediately
      const localPreviewUrl = URL.createObjectURL(file);
      
      // Insert temporary image with loading state
      const tempImageId = `temp-${Date.now()}-${Math.random()}`;
      editor.chain().focus().insertContent({
        type: 'image',
        attrs: {
          src: localPreviewUrl,
          alt: `Uploading: ${imageName}`,
          title: `Uploading: ${imageName}`,
          class: 'image-uploading',
          'data-temp-id': tempImageId,
        },
      }).run();
      
      try {
        // Use the uploadMedia utility from your project
        const uploaded = await uploadMedia(file);
        
        if (uploaded && uploaded.url) {
          imageUrl = uploaded.url;
          imageName = uploaded.fileName || file.name;
          console.log(imageUrl);
        } else {
          throw new Error('Upload failed: No URL returned from server');
        }
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        
        // Find and replace the temporary image with the final URL
        let tempImagePos = null;
        
        editor.state.doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs['data-temp-id'] === tempImageId) {
            tempImagePos = pos;
            return false;
          }
          return true;
        });
        
        if (tempImagePos !== null) {
          editor.chain()
            .focus()
            .setNodeSelection(tempImagePos)
            .setImage({ 
              src: imageUrl, 
              alt: imageName,
              title: imageName,
              class: '',
            })
            .run();
        } else {
          // If temp image not found, insert new one
          editor.chain().focus().setImage({ 
            src: imageUrl, 
            alt: imageName,
            title: imageName,
          }).run();
        }
        
        // Clean up local preview URL
        URL.revokeObjectURL(localPreviewUrl);
        
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        // Remove the temporary image on error
        let tempImagePos = null;
        
        editor.state.doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs['data-temp-id'] === tempImageId) {
            tempImagePos = pos;
            return false;
          }
          return true;
        });
        
        if (tempImagePos !== null) {
          editor.chain().focus().deleteRange({ from: tempImagePos, to: tempImagePos + 1 }).run();
        }
        
        throw uploadError;
      }
      
      setTimeout(() => {
        setUploadQueue(prev => prev.slice(1));
        setCurrentUpload(null);
        setUploadProgress(0);
      }, 300);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload image. Please try again.');
      setUploadQueue(prev => prev.slice(1));
      setCurrentUpload(null);
      setUploadProgress(0);
      
      setTimeout(() => setError(null), 5000);
    } finally {
      uploadAbortController.current = null;
    }
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const errors = [];
    
    Array.from(files).forEach(file => {
      try {
        validateImage(file);
        validFiles.push(file);
      } catch (error) {
        errors.push(error.message);
      }
    });
    
    if (errors.length > 0) {
      setError(errors.join(', '));
      setTimeout(() => setError(null), 5000);
    }
    
    if (validFiles.length > 0) {
      setUploadQueue(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (event) => {
    if (event.target.files?.length) {
      handleFiles(Array.from(event.target.files));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerImageUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = "image/*";
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (event.dataTransfer.files?.length) {
      handleFiles(Array.from(event.dataTransfer.files));
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };

  const ToolbarButton = ({ onClick, isActive, children, title, disabled = false }) => {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`toolbar-btn ${isActive ? "active" : ""}`}
        title={title}
        disabled={disabled}
        aria-label={title}
        aria-pressed={isActive}
      >
        {children}
      </button>
    );
  };

  const MenuButton = forwardRef(({ icon: Icon, label, isOpen, onClick, children }, ref) => {
    return (
      <div className="menu-container" ref={ref}>
        <button
          type="button"
          className={`menu-trigger ${isOpen ? "open" : ""}`}
          onClick={onClick}
          title={label}
          aria-label={label}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <Icon size={18} />
          <ChevronDown size={14} className="menu-chevron" />
        </button>
        {isOpen && (
          <div className="menu-dropdown" role="menu">
            {children}
          </div>
        )}
      </div>
    );
  });

  MenuButton.displayName = 'MenuButton';

  const MenuItem = ({ onClick, isActive, children }) => {
    return (
      <button
        type="button"
        className={`menu-item ${isActive ? "active" : ""}`}
        onClick={() => {
          onClick();
          setShowHeadingMenu(false);
          setShowListMenu(false);
          setShowAlignMenu(false);
        }}
        role="menuitem"
        aria-current={isActive ? "true" : undefined}
      >
        {children}
      </button>
    );
  };

  const getHeadingIcon = () => {
    if (editor.isActive("heading", { level: 1 })) return Heading1;
    if (editor.isActive("heading", { level: 2 })) return Heading2;
    if (editor.isActive("heading", { level: 3 })) return Heading3;
    if (editor.isActive("heading", { level: 4 })) return Heading4;
    if (editor.isActive("heading", { level: 5 })) return Heading5;
    return Type;
  };

  const getAlignIcon = () => {
    if (editor.isActive({ textAlign: "center" })) return AlignCenter;
    if (editor.isActive({ textAlign: "right" })) return AlignRight;
    if (editor.isActive({ textAlign: "justify" })) return AlignJustify;
    return AlignLeft;
  };

  return (
    <div 
      className={`simple-editor-wrapper ${dragActive ? "drag-active" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="simple-editor-container">
        {/* Toolbar */}
        <div className="editor-toolbar">
          <div className="toolbar-inner">
            
            {/* Undo/Redo */}
            <div className="toolbar-group" role="group" aria-label="Undo/Redo">
              <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                title="Undo (Ctrl+Z)"
                disabled={!editor.can().undo()}
              >
                <Undo2 size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                title="Redo (Ctrl+Y)"
                disabled={!editor.can().redo()}
              >
                <Redo2 size={18} />
              </ToolbarButton>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Text Formatting */}
            <div className="toolbar-group" role="group" aria-label="Text formatting">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive("bold")}
                title="Bold (Ctrl+B)"
              >
                <Bold size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive("italic")}
                title="Italic (Ctrl+I)"
              >
                <Italic size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                isActive={editor.isActive("underline")}
                title="Underline (Ctrl+U)"
              >
                <UnderlineIcon size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive("strike")}
                title="Strikethrough"
              >
                <Strikethrough size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                isActive={editor.isActive("highlight")}
                title="Highlight"
              >
                <Highlighter size={18} />
              </ToolbarButton>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Headings Menu */}
            <MenuButton
              icon={getHeadingIcon()}
              label="Headings"
              isOpen={showHeadingMenu}
              onClick={() => setShowHeadingMenu(!showHeadingMenu)}
              ref={headingMenuRef}
            >
              <MenuItem
                key="paragraph"
                onClick={() => editor.chain().focus().setParagraph().run()}
                isActive={editor.isActive("paragraph")}
              >
                <Type size={16} />
                <span>Paragraph</span>
              </MenuItem>
              <div className="menu-divider" key="divider1" />
              {[1, 2, 3, 4, 5].map(level => (
                <MenuItem
                  key={`heading-${level}`}
                  onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                  isActive={editor.isActive("heading", { level })}
                >
                  {level === 1 && <Heading1 size={16} />}
                  {level === 2 && <Heading2 size={16} />}
                  {level === 3 && <Heading3 size={16} />}
                  {level === 4 && <Heading4 size={16} />}
                  {level === 5 && <Heading5 size={16} />}
                  <span>Heading {level}</span>
                </MenuItem>
              ))}
            </MenuButton>

            {/* Lists Menu */}
            <MenuButton
              icon={List}
              label="Lists"
              isOpen={showListMenu}
              onClick={() => setShowListMenu(!showListMenu)}
              ref={listMenuRef}
            >
              <MenuItem
                key="bullet-list"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive("bulletList")}
              >
                <List size={16} />
                <span>Bullet List</span>
              </MenuItem>
              <MenuItem
                key="ordered-list"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive("orderedList")}
              >
                <ListOrdered size={16} />
                <span>Numbered List</span>
              </MenuItem>
              <div className="menu-divider" key="divider2" />
              <MenuItem
                key="quote"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive("blockquote")}
              >
                <Quote size={16} />
                <span>Quote</span>
              </MenuItem>
              <MenuItem
                key="code-block"
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                isActive={editor.isActive("codeBlock")}
              >
                <Code2 size={16} />
                <span>Code Block</span>
              </MenuItem>
            </MenuButton>

            {/* Alignment Menu */}
            <MenuButton
              icon={getAlignIcon()}
              label="Alignment"
              isOpen={showAlignMenu}
              onClick={() => setShowAlignMenu(!showAlignMenu)}
              ref={alignMenuRef}
            >
              {[
                { align: "left", icon: AlignLeft, label: "Align Left" },
                { align: "center", icon: AlignCenter, label: "Align Center" },
                { align: "right", icon: AlignRight, label: "Align Right" },
                { align: "justify", icon: AlignJustify, label: "Justify" },
              ].map(({ align, icon: Icon, label }) => (
                <MenuItem
                  key={align}
                  onClick={() => editor.chain().focus().setTextAlign(align).run()}
                  isActive={editor.isActive({ textAlign: align })}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </MenuItem>
              ))}
            </MenuButton>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Links */}
            <div className="toolbar-group" role="group" aria-label="Links">
              <ToolbarButton
                onClick={setLink}
                isActive={editor.isActive("link")}
                title="Add Link (Ctrl+K)"
              >
                <Link2 size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().unsetLink().run()}
                title="Remove Link"
                disabled={!editor.isActive("link")}
              >
                <Unlink size={18} />
              </ToolbarButton>
            </div>

            <div className="toolbar-divider" aria-hidden="true" />

            {/* Image Upload Button */}
            <div className="toolbar-group" role="group" aria-label="Image upload">
              <ToolbarButton
                onClick={triggerImageUpload}
                title="Upload Image (Max 5MB)"
              >
                <ImageIcon size={18} />
              </ToolbarButton>
            </div>
          </div>
        </div>

        {/* Upload Progress */}
        {(currentUpload || uploadQueue.length > 0) && (
          <div className="upload-progress" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100">
            <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
            <div className="upload-progress-text">
              {currentUpload ? `Uploading ${currentUpload.name}... ${uploadProgress}%` : `Queue: ${uploadQueue.length} images remaining`}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="upload-error" role="alert">
            <X size={18} />
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss error">×</button>
          </div>
        )}

        {/* Drag and Drop Overlay */}
        {dragActive && (
          <div className="drag-overlay" aria-live="polite">
            <div className="drag-overlay-content">
              <Upload size={48} />
              <h3>Drop images here</h3>
              <p>Upload JPEG, PNG, GIF, WEBP, or SVG images (Max 5MB)</p>
            </div>
          </div>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          aria-hidden="true"
        />

        {/* Editor Content */}
        <div className="editor-content-wrapper">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
};

export default SimpleTiptapEditor;